import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Button } from '../components/v2';
import { apiFetch } from '../services/api';

/**
 * Consent screen for connecting an application to a governed agent.
 *
 * An application sends the person here (via the authorization endpoint) to ask permission to act on
 * their behalf against one agent. We confirm who is asking and what they'll reach, then record the
 * person's decision. The decision is submitted through apiFetch so the platform CSRF token travels
 * with it; on success the person is returned to the application.
 */

// The authorization parameters we carry through unchanged from the incoming link to the two calls.
const OAUTH_PARAM_KEYS = [
  'client_id', 'redirect_uri', 'response_type', 'code_challenge',
  'code_challenge_method', 'resource', 'agent_id', 'scope', 'state',
];

export default function OAuthConsentPage() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { addToast } = useToast();

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(true);

  // Not signed in → send to sign-in, remembering where to come back to.
  useEffect(() => {
    if (authLoading || isAuthenticated) return;
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.replace(`/login?returnTo=${returnTo}`);
  }, [authLoading, isAuthenticated]);

  const buildBody = useCallback((extra) => {
    const body = { ...extra };
    OAUTH_PARAM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v != null) body[k] = v;
    });
    return body;
  }, [params]);

  // Load the details the screen shows (application + agent).
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const qs = OAUTH_PARAM_KEYS
          .filter((k) => params.get(k) != null)
          .map((k) => `${k}=${encodeURIComponent(params.get(k))}`)
          .join('&');
        const res = await apiFetch(`/api/v1/oauth/authorize/consent-info?${qs}`);
        if (cancelled) return;
        // apiFetch resolves to an envelope { ok, data } — read the body from res.data (and surface a
        // failed check rather than rendering an empty card).
        if (!res?.ok) {
          setError(res?.error || 'This connection request could not be verified. Please try again from the application.');
        } else {
          setInfo(res.data);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'This connection request could not be verified. Please try again from the application.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, isAuthenticated, params]);

  const decide = useCallback(async (approve) => {
    setBusy(true);
    try {
      const res = await apiFetch('/api/v1/oauth/authorize/decision', 'POST', buildBody({ approve, remember }));
      // Envelope: the redirect target is res.data.redirect (not res.redirect).
      const redirectTo = res?.data?.redirect;
      if (res?.ok && redirectTo) {
        window.location.href = redirectTo;
      } else {
        setBusy(false);
        setError('We could not complete this request. Please try again from the application.');
      }
    } catch (e) {
      setBusy(false);
      addToast?.({ tone: 'danger', title: 'Could not complete', description: e?.message || 'Please try again.' });
    }
  }, [buildBody, remember, addToast]);

  if (authLoading || !isAuthenticated) {
    return <CenteredMessage>Loading…</CenteredMessage>;
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <div style={iconWrapStyle}><ShieldCheck size={24} /></div>
        </div>

        {error ? (
          <>
            <h1 style={titleStyle}>Request could not be verified</h1>
            <p style={bodyStyle}>{error}</p>
          </>
        ) : !info ? (
          <CenteredMessage>Checking this request…</CenteredMessage>
        ) : (
          <>
            <h1 style={titleStyle}>Allow access?</h1>
            {info.surface ? (
              <>
                <p style={bodyStyle}>
                  <strong>{info.application_name}</strong> is asking to act on your behalf across the
                  agents you're allowed to use.
                </p>
                <p style={subtleStyle}>
                  It will be able to use only the tools you're granted, on each of those agents. Every
                  action stays protected and is recorded.
                </p>
              </>
            ) : (
              <>
                <p style={bodyStyle}>
                  <strong>{info.application_name}</strong> is asking to act on your behalf in{' '}
                  <strong>{info.agent_name}</strong>.
                </p>
                <p style={subtleStyle}>
                  It will be able to use only the tools you're allowed to use there. Every action stays
                  protected and is recorded.
                </p>
              </>
            )}

            {!info.surface && (
              <label style={rememberStyle}>
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                Don't ask again for this application and agent
              </label>
            )}

            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <Button variant="secondary" fullWidth disabled={busy} onClick={() => decide(false)}>
                Deny
              </Button>
              <Button variant="primary" fullWidth loading={busy} onClick={() => decide(true)}>
                Allow
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CenteredMessage({ children }) {
  return <div style={{ ...pageStyle, color: '#52525b', fontSize: 13 }}>{children}</div>;
}

// Auth-flow pages render outside the app shell, so they carry their own light layout (as LoginPage does).
const pageStyle = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: '#f4f4f5', padding: 24,
};
const cardStyle = {
  width: '100%', maxWidth: 420, background: '#ffffff', borderRadius: 16,
  border: '1px solid #e4e4e7', padding: 32, boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};
const iconWrapStyle = {
  width: 48, height: 48, borderRadius: '9999px', background: '#eef2ff', color: '#4f46e5',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const titleStyle = { fontSize: 18, fontWeight: 600, textAlign: 'center', margin: '0 0 12px', color: '#18181b' };
const bodyStyle = { fontSize: 14, textAlign: 'center', margin: '0 0 8px', color: '#3f3f46', lineHeight: 1.5 };
const subtleStyle = { fontSize: 12.5, textAlign: 'center', margin: '0 0 4px', color: '#71717a', lineHeight: 1.5 };
const rememberStyle = { display: 'flex', alignItems: 'center', fontSize: 13, color: '#3f3f46', marginTop: 18 };
